import { layoutRect, viewportHeight as layoutViewportHeight } from "../../utils/displayDensity";
import React, {
  useRef,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { components } from "react-select";
import { useVirtualizer } from "@tanstack/react-virtual";
import OptionRow from "./OptionRow";
import { DEFAULT_OPTION_HEIGHT } from "./virtualizedDropdownStyles";

const VirtualizedMenuList = (props) => {
  const parentRef = useRef(null);
  const scrollTimeoutRef = useRef();
  const childrenArray = useMemo(
    () => React.Children.toArray(props.children),
    [props.children]
  );

  const [measuredHeights, setMeasuredHeights] = useState({});
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const [maxHeight, setMaxHeight] = useState(props.maxHeight);
  const [contentHeight, setContentHeight] = useState(0);

  const calculateContentHeight = useCallback(() => {
    if (Object.keys(measuredHeights).length === 0)
      return childrenArray.length * DEFAULT_OPTION_HEIGHT;

    return childrenArray.reduce((total, _, index) => {
      return total + (measuredHeights[index] || DEFAULT_OPTION_HEIGHT);
    }, 0);
  }, [childrenArray.length, measuredHeights]);

  const calculateMaxHeight = useCallback(() => {
    const menuListElement =
      parentRef.current?.closest?.(".rs__menu-list") || parentRef.current;
    if (!menuListElement) return props.maxHeight;

    const rect = layoutRect(menuListElement);
    const windowHeight = layoutViewportHeight();
    const availableSpaceBelow = windowHeight - rect.top - 10;
    const availableSpaceAbove = rect.top - 10;

    const availableSpace = Math.max(availableSpaceBelow, availableSpaceAbove);

    const currentContentHeight = calculateContentHeight();

    if (
      currentContentHeight <= availableSpace &&
      currentContentHeight <= props.maxHeight
    ) {
      return "auto";
    } else {
      const calculatedHeight = Math.min(
        props.maxHeight,
        Math.max(100, availableSpace)
      );
      return calculatedHeight;
    }
  }, [props.maxHeight, calculateContentHeight]);

  useEffect(() => {
    const newContentHeight = calculateContentHeight();
    setContentHeight(newContentHeight);
  }, [calculateContentHeight]);

  useEffect(() => {
    const updateMaxHeight = () => {
      const newMaxHeight = calculateMaxHeight();
      setMaxHeight(newMaxHeight);
    };

    updateMaxHeight();

    window.addEventListener("resize", updateMaxHeight);

    const timeoutId = setTimeout(updateMaxHeight, 0);

    return () => {
      window.removeEventListener("resize", updateMaxHeight);
      clearTimeout(timeoutId);
    };
  }, [calculateMaxHeight]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setMaxHeight(calculateMaxHeight());
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [childrenArray.length, calculateMaxHeight, contentHeight]);

  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScrollStart = () => {
      setIsScrolling(true);
      setIsMeasuring(false);
      clearTimeout(scrollTimeoutRef.current);
    };

    const handleScrollEnd = () => {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        setIsMeasuring(true);
      }, 150);
    };

    scrollElement.addEventListener("scroll", handleScrollStart, {
      passive: true,
    });
    scrollElement.addEventListener("scroll", handleScrollEnd, {
      passive: true,
    });

    return () => {
      scrollElement.removeEventListener("scroll", handleScrollStart);
      scrollElement.removeEventListener("scroll", handleScrollEnd);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const setMeasuredHeight = useCallback(
    (index, height) => {
      if (
        !isScrolling &&
        Math.abs((measuredHeights[index] || DEFAULT_OPTION_HEIGHT) - height) > 1
      ) {
        setMeasuredHeights((prev) => ({
          ...prev,
          [index]: height,
        }));
      }
    },
    [isScrolling, measuredHeights]
  );

  const estimateSize = useCallback(
    (index) => measuredHeights[index] || DEFAULT_OPTION_HEIGHT,
    [measuredHeights]
  );

  const rowVirtualizer = useVirtualizer({
    count: childrenArray.length,
    getScrollElement: () => parentRef.current,
    estimateSize,
    overscan: 100,
    getItemKey: (index) =>
      childrenArray[index]?.key ??
      childrenArray[index]?.props?.data?.value ??
      index,
  });

  useEffect(() => {
    setMeasuredHeights({});
  }, [childrenArray.length]);

  const needsScroll = maxHeight !== "auto" && contentHeight > maxHeight;

  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        ref: parentRef,
        style: {
          maxHeight: maxHeight,
          height: maxHeight === "auto" ? "auto" : undefined,
          overflowY: needsScroll ? "auto" : "hidden",
          overflowX: "hidden",
          position: "relative",
          willChange: "transform",
          scrollBehavior: "auto",
        },
      }}
    >
      <style>{`
        .rs-option {
          background: #111;
          color: #fff;
          user-select: none;
          transition: background 0.1s ease;
        }
        .rs-option:hover { background: #222 !important; }
        .rs-option.rs-selected { background: #444 !important; }
        .rs-option .rs__option-label {
          white-space: normal;
          word-break: break-word;
          line-height: 1.4;
          width: 100%;
          }
         .rs__menu-list {
          scrollbar-color: #9f9f9f #2c2c2c;
        }
        .rs__menu-list::-webkit-scrollbar {
          width: 8px;
        }
        .rs__menu-list::-webkit-scrollbar-track {
          background: #2c2c2c;
        }
        .rs__menu-list::-webkit-scrollbar-thumb {
          background-color: #9f9f9f;
          border-radius: 4px;
        }
        .rs__menu-list::-webkit-scrollbar-thumb:hover {
          background-color: #bfbfbf;
        }
      `}</style>

      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const option = childrenArray[virtualRow.index];
          if (!option) return null;
          return (
            <OptionRow
              key={virtualRow.key}
              optionProps={option.props}
              virtualRow={virtualRow}
              onPreview={props.selectProps?.onPreview}
              previewType={props.selectProps?.previewType}
              measuredHeights={measuredHeights}
              setMeasuredHeight={setMeasuredHeight}
              isMeasuring={isMeasuring}
              showHighlightToggle={props.selectProps?.showHighlightToggle}
              isHighlighted={props.selectProps?.isHighlighted}
              onToggleHighlight={props.selectProps?.onToggleHighlight}
            />
          );
        })}
      </div>
    </components.MenuList>
  );
};

export default VirtualizedMenuList;
